import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { publicAPI } from '../services/api'
import { localizeApiDetail } from '../i18n/apiError'
import { dateLocale } from '../i18n/format'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { sanitizeHtml } from '../utils/sanitizeHtml'
import { Megaphone, BookOpen, ClipboardList } from 'lucide-react'

interface Announcement {
  id: number
  title: string
  content: string
  content_type: string
  priority: number
  is_active: boolean
  publish_date: string
  expiry_date: string | null
  severity: 'info' | 'success' | 'warning' | 'error'
  created_by: string
  created_time: string
  modified_time: string
}

type TabType = 'instruction' | 'news' | 'data_change'

export default function Announcements() {
  const { t } = useTranslation('account')
  const [activeTab, setActiveTab] = useState<TabType>('instruction')
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAnnouncements()
  }, [activeTab])

  const fetchAnnouncements = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch announcements with a wide date range to include all data, including 2026
      const response = await publicAPI.getAnnouncements({
        content_type: activeTab,
        start_date: '2020-01-01',
        end_date: '2030-12-31',
        include_expired: activeTab === 'data_change' ? true : false
      })
      setAnnouncements(response.data.data || [])
    } catch (err: any) {
      console.error('Error fetching announcements:', err)
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('announcements.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(dateLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'info':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/10'
      case 'success':
        return 'border-green-500 bg-green-50 dark:bg-green-900/10'
      case 'warning':
        return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10'
      case 'error':
        return 'border-red-500 bg-red-50 dark:bg-red-900/10'
      default:
        return 'border-gray-500 bg-gray-50 dark:bg-gray-900/10'
    }
  }

  const getSeverityBadge = (severity: string) => {
    const baseClasses = 'px-2 py-1 rounded-sm text-xs font-medium'
    switch (severity) {
      case 'info':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300`
      case 'success':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300`
      case 'warning':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300`
      case 'error':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300`
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300`
    }
  }

  const renderPrettyCards = () => {
    if (announcements.length === 0) {
      return (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {activeTab === 'news' ? t('announcements.empty.news') : activeTab === 'data_change' ? t('announcements.empty.dataChange') : t('announcements.empty.instruction')}
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {announcements.map((announcement) => (
          <div
            key={announcement.id}
            className={`card p-6 border-l-4 transition-shadow hover:shadow-lg ${getSeverityColor(announcement.severity)}`}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {announcement.title}
              </h3>
              <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap ml-4">
                {formatDate(announcement.publish_date)}
              </span>
            </div>
            <div
              className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(announcement.content) }}
            />
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className={getSeverityBadge(announcement.severity)}>
                {announcement.severity}
              </span>
              <span>•</span>
              <span>{t('announcements.author', { name: announcement.created_by })}</span>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) return <ErrorMessage message={error} />

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Megaphone className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('announcements.title')}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {t('announcements.subtitle')}
        </p>
      </div>

      {/* Content */}
      <div className="card">
        <div className="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-dark-border">
          <button
            type="button"
            onClick={() => setActiveTab('instruction')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'instruction'
                ? 'bg-primary-600 text-white dark:bg-primary-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-hover'
            }`}
            aria-pressed={activeTab === 'instruction'}
          >
            <BookOpen className="w-4 h-4" />
            {t('announcements.tabs.instruction')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('news')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'news'
                ? 'bg-primary-600 text-white dark:bg-primary-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-hover'
            }`}
            aria-pressed={activeTab === 'news'}
          >
            <Megaphone className="w-4 h-4" />
            {t('announcements.tabs.news')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('data_change')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'data_change'
                ? 'bg-primary-600 text-white dark:bg-primary-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-hover'
            }`}
            aria-pressed={activeTab === 'data_change'}
          >
            <ClipboardList className="w-4 h-4" />
            {t('announcements.tabs.dataChange')}
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <LoadingSpinner />
          ) : (
            renderPrettyCards()
          )}
        </div>
      </div>
    </div>
  )
}
